import { motion } from "framer-motion";
import { Bell, BellOff, BellRing, Loader2, AlertTriangle } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import usePushNotifications from "../lib/usePushNotifications";

const STATUS_COPY = {
    granted: {
        icon: BellRing,
        title: "Notifications activées",
        body: "Tu recevras un rappel à 19h si tu n'as pas joué, ainsi que tes badges, validations et résultats hebdo.",
        color: "text-emerald-400",
        bg: "bg-emerald-400/10",
        border: "border-emerald-400/20",
    },
    prompt: {
        icon: Bell,
        title: "Active les notifications",
        body: "Active-les pour ne rater aucune cooptation validée, badge, résultat hebdo ou rappel de série.",
        color: "text-shariti-orange-light",
        bg: "bg-shariti-orange/10",
        border: "border-shariti-orange/20",
    },
    denied: {
        icon: AlertTriangle,
        title: "Notifications bloquées",
        body: "Ton navigateur a refusé. Pour les activer : icône cadenas dans l'URL → Notifications → Autoriser.",
        color: "text-red-400",
        bg: "bg-red-400/10",
        border: "border-red-400/20",
    },
    unsupported: {
        icon: BellOff,
        title: "Non supporté",
        body: "Ton navigateur ou ton appareil ne supporte pas les notifications push.",
        color: "text-white/40",
        bg: "bg-white/5",
        border: "border-white/5",
    },
    loading: {
        icon: Loader2,
        title: "Chargement…",
        body: "",
        color: "text-white/50",
        bg: "bg-white/5",
        border: "border-white/5",
    },
    idle: {
        icon: Bell,
        title: "Notifications",
        body: "Active-les pour rester motivé.",
        color: "text-white/50",
        bg: "bg-white/5",
        border: "border-white/5",
    },
};

const KIND_LIST = [
    { label: "🔥 Rappel de série quotidien à 19h", on: true },
    { label: "🏆 Résultat hebdomadaire du classement (dimanche soir)", on: true },
    { label: "🥇 Badge débloqué + cap de points franchi", on: true },
    { label: "✅ Preuve d'engagement validée / refusée", on: true },
    { label: "🤝 Cooptation acceptée + bonus crédité", on: true },
    { label: "📌 Astuce épinglée par un manager ou admin", on: true },
    { label: "👀 Relance d'inactivité après 3 jours", on: true },
];

export default function NotificationSettingsCard() {
    const { status, subscribed, enable, disable, sendTest, supported } =
        usePushNotifications();
    const [busy, setBusy] = useState(false);

    const meta = STATUS_COPY[status] || STATUS_COPY.idle;
    const Icon = meta.icon;
    const isOn = status === "granted" && subscribed;

    const handleToggle = async () => {
        if (busy) return;
        setBusy(true);
        try {
            if (isOn) {
                await disable();
                toast.success("Notifications désactivées");
            } else {
                const result = await enable();
                if (result?.ok) {
                    toast.success("Notifications activées 🔔");
                } else {
                    const reason = result?.reason || "unknown";
                    const messages = {
                        unsupported: "Ton navigateur ne supporte pas les notifications.",
                        no_vapid_key: "Configuration serveur manquante (VAPID).",
                        sw_register_failed: "Service Worker introuvable. Recharge la page (Ctrl+R) puis réessaie.",
                        permission_denied: "Tu as refusé. Ouvre les réglages du navigateur → Notifications → Autoriser pour ce site.",
                        permission_default: "Permission non accordée. Réessaie et clique « Autoriser ».",
                        subscribe_failed: "Ton navigateur (souvent Brave avec Shields, ou mode privé) bloque les push. Désactive Shields sur ce site puis réessaie.",
                        persist_failed: "Impossible de sauvegarder côté serveur. Vérifie ta connexion.",
                        unexpected: "Erreur inattendue. Recharge la page et réessaie.",
                    };
                    toast.error(messages[reason] || "Activation impossible.");
                }
            }
        } finally {
            setBusy(false);
        }
    };

    const handleTest = async () => {
        if (busy) return;
        setBusy(true);
        try {
            const ok = await sendTest();
            if (ok) toast.success("Notification de test envoyée 📬");
            else toast.error("Envoi du test impossible.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <motion.div
            id="notifications"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card rounded-3xl p-5 sm:p-6"
            data-testid="notifications-settings-card"
        >
            <div className="flex items-start gap-4 mb-4">
                <div
                    className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${meta.bg} border ${meta.border}`}
                >
                    <Icon
                        className={`w-5 h-5 ${meta.color} ${
                            status === "loading" ? "animate-spin" : ""
                        }`}
                    />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-xs font-thin tracking-[0.25em] uppercase text-shariti-orange-light mb-1">
                        Notifications push
                    </p>
                    <h3 className="text-lg font-extrabold text-white tracking-tight leading-snug">
                        {meta.title}
                    </h3>
                    {meta.body && (
                        <p className="text-sm text-white/55 font-light leading-relaxed mt-1">
                            {meta.body}
                        </p>
                    )}
                </div>
            </div>

            {supported && status !== "unsupported" && (
                <div className="flex flex-col sm:flex-row gap-2 mt-4">
                    <button
                        onClick={handleToggle}
                        disabled={busy || status === "denied" || status === "loading"}
                        data-testid="notif-settings-toggle"
                        className={`flex-1 inline-flex items-center justify-center gap-2 font-extrabold py-3 px-4 rounded-2xl transition-all ${
                            isOn
                                ? "bg-white/5 text-white/70 border border-white/10 hover:bg-white/10"
                                : "bg-shariti-orange text-white orange-glow hover:bg-shariti-orange-light"
                        } disabled:opacity-50`}
                    >
                        {busy ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : isOn ? (
                            <BellOff className="w-4 h-4" />
                        ) : (
                            <BellRing className="w-4 h-4" />
                        )}
                        {isOn ? "Désactiver" : "Activer les notifications"}
                    </button>
                    {isOn && (
                        <button
                            onClick={handleTest}
                            disabled={busy}
                            data-testid="notif-settings-test"
                            className="inline-flex items-center justify-center gap-2 font-bold py-3 px-4 rounded-2xl bg-white/5 text-white/70 border border-white/10 hover:bg-white/10 transition-all disabled:opacity-50"
                        >
                            <Bell className="w-4 h-4" />
                            Test
                        </button>
                    )}
                </div>
            )}

            <div className="mt-5 pt-5 border-t border-white/5">
                <p className="text-[10px] uppercase tracking-widest font-bold text-white/40 mb-3">
                    Tu seras notifié·e pour :
                </p>
                <ul className="space-y-1.5">
                    {KIND_LIST.map((k, i) => (
                        <li
                            key={i}
                            className="text-sm text-white/65 font-light leading-relaxed"
                        >
                            {k.label}
                        </li>
                    ))}
                </ul>
            </div>
        </motion.div>
    );
}
